import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { FichaCadastralPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/ficha-cadastral-pj/ficha-cadastral-pj.service';
import { ReportFichaCadastralPjRequest, ReportFichaCadastralPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/ficha-cadastral-pj/ficha-cadastral-pj.model';
import { FichaCadastralPjJobService } from '@/core/services/ficha-cadastral-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportFichaCadastralPjRequest;
type Resp = ReportFichaCadastralPjResponse;

@Component({
    selector: 'app-ficha-cadastral-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, FormsModule, Button, Checkbox, RelatorioContainer, CanDirective],
    templateUrl: './ficha-cadastral-pj.html',
    styleUrl: './ficha-cadastral-pj.scss'
})
/**
 * Relatório PDF assíncrono da Ficha Cadastral do prestador (PJ).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`) e a opção de incluir o corpo clínico.
 */
export class ReportFichaCadastralPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly relatorioService = inject(FichaCadastralPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly jobService = inject(FichaCadastralPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.jobService.job$;

    /** Inclui a relação do corpo clínico na ficha (checkbox `[(ngModel)]` no template). */
    incluirCorpoClinico = true;

    protected get jobFacade(): RelatorioJobFacade {
        return this.jobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a ficha.'
            });
            return null;
        }

        return { nuCrm, sgUf, incluirCorpoClinico: this.incluirCorpoClinico };
    }

    protected exportar(request: Req) {
        return this.relatorioService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.incluirCorpoClinico = true;
        this.limparJob();
    }
}
